import React, { useState } from 'react';
import { FetchJobResult, ValidateResult } from '../../../services/refinitivService';
import { useRefinitivJob } from './useRefinitivJob';
import { FetchJobSummary, JobStatusLine, ValidateTable } from './JobStatusLine';

/** Edit drawer: check the saved config against Refinitiv, load history, fetch now — all run by the VM worker. */
const ContractRefinitivActions: React.FC<{ contractId: string; fetchEnabled: boolean; workerOnline?: boolean; onDataChanged?: () => void }> = ({
  contractId, fetchEnabled, workerOnline, onDataChanged,
}) => {
  const check = useRefinitivJob<ValidateResult>();
  const fetchJob = useRefinitivJob<FetchJobResult>();
  const [startDate, setStartDate] = useState('2025-01-01');
  const id = Number(contractId);

  const done = fetchJob.job?.status === 'done' ? fetchJob.job.result : null;
  React.useEffect(() => {
    if (done) onDataChanged?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">Refinitiv</h3>
      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Do the RICs this contract requests exist today?</span>
            <button
              type="button"
              disabled={check.busy}
              onClick={() => check.start('validate', { contractId: id })}
              className="px-3 py-1.5 text-sm rounded border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
            >
              Check with Refinitiv
            </button>
          </div>
          <div className="mt-2">
            <JobStatusLine job={check.job} error={check.error} workerOnline={workerOnline} />
            {check.job?.status === 'done' && check.job.result && <ValidateTable result={check.job.result} />}
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm text-gray-700">Load history from</span>
            <div className="flex items-center gap-2">
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-sm" />
              <button
                type="button"
                disabled={!fetchEnabled || fetchJob.busy}
                onClick={() => fetchJob.start('backfill', { contractId: id, startDate })}
                className="px-3 py-1.5 text-sm rounded border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
              >
                Load history
              </button>
              <button
                type="button"
                disabled={!fetchEnabled || fetchJob.busy}
                onClick={() => fetchJob.start('fetch_now', { contractId: id })}
                className="px-3 py-1.5 text-sm rounded border border-gray-400 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Fetch now
              </button>
            </div>
          </div>
          {!fetchEnabled && <p className="text-xs text-amber-700 mt-1">Enable fetching first.</p>}
          <p className="text-xs text-gray-500 mt-1">Only dates after the newest stored price are fetched; existing prices are not overwritten.</p>
          <div className="mt-2">
            <JobStatusLine job={fetchJob.job} error={fetchJob.error} workerOnline={workerOnline} />
            {done && <FetchJobSummary result={done} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractRefinitivActions;
